import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ArticleCardProps } from 'components/BaseHelpers/ArticleCard';
import { LocationCardProps } from 'components/BaseHelpers/LocationCard';
import { PeopleCardProps } from 'components/BaseHelpers/PeopleCard';

import { flattenFilterOptions } from '../../../hooks/useSearchPage/filterOptions';
import {
  getAppliedCriteria,
  submitSearchCriteriaQuery,
} from '../../../hooks/useSearchPage/searchCriteria/state';
import {
  appendSearchCriteriaParams,
  readSearchCriteriaUrlStateFromAsPath,
} from '../../../hooks/useSearchPage/searchCriteria/urlState';
import { useSearchCriteria } from '../../../hooks/useSearchPage/searchCriteria/useSearchCriteria';
import {
  createSearchListExecutionState,
  resetSearchListExecutionForCriteriaChange,
  resetSearchListExecutionForSortChange,
  resolveSearchListLoadMoreCursor,
  resolveSearchListLoadMoreError,
  resolveSearchListLoadMoreSuccess,
  resolveSearchListReloadError,
  resolveSearchListReloadSuccess,
  startSearchListLoadMore,
  startSearchListReload,
} from '../../../hooks/useSearchPage/searchListState/executionState';
import {
  MultiListResultState,
  MultiListSearchDefinition,
  SearchListExecutionState,
  SearchListUrlState,
} from '../../../hooks/useSearchPage/searchListState/types';
import {
  appendSearchListParams,
  readSearchListUrlStateFromAsPath,
  resolveSearchListParamNames,
} from '../../../hooks/useSearchPage/searchListState/urlState';
import { useMultiSearchListState } from '../../../hooks/useSearchPage/searchListState/useMultiSearchListState';
import {
  serializeCriteria,
  serializeListUrlState,
} from '../../../hooks/useSearchPage/serialization';
import {
  SearchPageFilterDefinition,
  SearchPageSortOption,
} from '../../../hooks/useSearchPage/sharedTypes';
import { useSearchPageUrlSync } from '../../../hooks/useSearchPage/urlState';
import {
  useSearchFacetsState,
  type SearchFacetsFetcher,
} from '../../../hooks/useSearchPage/useSearchFacetsState';
import { useSearchPageCriteriaController } from '../../../hooks/useSearchPage/useSearchPageCriteriaController';
import { useSite } from '../../../hooks/useSite';
import { LinkData, SearchFilterConfig } from '../../../types/components';

export const siteSearchFilterNames = ['services', 'offices'] as const;
export const siteSearchListKeys = ['people', 'services', 'newsinsights', 'resources'] as const;

export type SiteSearchFilterName = (typeof siteSearchFilterNames)[number];
export type SiteSearchListKey = (typeof siteSearchListKeys)[number];
export type SiteSearchSortValue = 'relevance' | 'date';
export type SiteSearchMode = 'overview' | 'detail';

export type SiteSearchResultItem =
  | ({
      postType: 'people';
    } & PeopleCardProps)
  | ({
      postType: 'newsinsights' | 'resources';
    } & ArticleCardProps)
  | ({
      postType: 'services';
    } & LinkData)
  | ({
      postType: 'office';
    } & LocationCardProps);

type SiteSearchListDefinition = MultiListSearchDefinition<
  SiteSearchResultItem,
  SiteSearchFilterName,
  SiteSearchSortValue,
  number | null
>;

type SiteSearchLists = Record<SiteSearchListKey, SiteSearchListDefinition>;

type UseSiteSearchConfig = {
  previewResultsPerPage: Record<SiteSearchListKey, number>;
  detailResultsPerPage: Record<SiteSearchListKey, number>;
  filters: SearchFilterConfig[];
};

type SiteSearchViewState = {
  activeTab: SiteSearchListKey | null;
};

type SiteSearchUrlState = {
  criteria: {
    query: string;
    filters: Record<SiteSearchFilterName, string[]>;
  };
  activeTab: SiteSearchListKey | null;
  sharedSort: SiteSearchSortValue | null;
  activeDetailTake: number | null;
};

type SiteSearchDetailStateResult = {
  listsState: Record<
    SiteSearchListKey,
    SearchListExecutionState<
      SiteSearchResultItem,
      SiteSearchFilterName,
      SiteSearchSortValue,
      number | null
    >
  >;
  applyUrlState: (
    criteria: SiteSearchUrlState['criteria'],
    activeTab: SiteSearchListKey | null,
    sharedSort: SiteSearchSortValue | null,
    activeDetailTake: number | null
  ) => void;
  ensureTabLoaded: (
    criteria: SiteSearchUrlState['criteria'],
    activeTab: SiteSearchListKey | null
  ) => void;
  reloadAfterCriteriaChange: (criteria: SiteSearchUrlState['criteria']) => void;
  resetAllAndReload: (criteria: SiteSearchUrlState['criteria']) => void;
  loadMore: (listKey: SiteSearchListKey) => void;
  setSharedSort: (nextSort: SiteSearchSortValue | null) => void;
};

const siteSearchTabParamName = 'tab';

export const siteSearchSortOptions: SearchPageSortOption<SiteSearchSortValue>[] = [
  {
    label: 'Relevance',
    value: 'relevance',
  },
  {
    label: 'Date',
    value: 'date',
  },
];

/**
 * Site search intentionally composes the shared search primitives directly instead of routing its
 * advanced overview/detail behavior back through the generic public hooks.
 *
 * Why:
 * - overview mode and focused detail mode have different execution needs
 * - focused tab and focused depth are URL-backed view concerns
 * - keeping this logic local prevents generic single/multi-list hooks from becoming mode-heavy
 *
 * Maintain here when changing:
 * - tab/view behavior
 * - overview vs detail fetch orchestration
 * - site-search-specific URL contract such as `tab` and namespaced `take-<listKey>`
 */

const toFilterDefinitions = (
  filters: SearchFilterConfig[]
): Record<SiteSearchFilterName, SearchPageFilterDefinition> =>
  filters.reduce<Record<SiteSearchFilterName, SearchPageFilterDefinition>>(
    (definitions, filter) => {
      if (filter.name !== 'services' && filter.name !== 'offices') {
        return definitions;
      }

      definitions[filter.name] = {
        label: filter.label,
        options: flattenFilterOptions(filter.options),
      };

      return definitions;
    },
    {
      services: {
        label: 'Services',
        options: [],
      },
      offices: {
        label: 'Location',
        options: [],
      },
    }
  );

const getSharedDisplayedSort = (query: string, sort: SiteSearchSortValue | null) =>
  sort ?? (query ? 'relevance' : 'date');

const resolveSharedSortFromOverviewState = (
  listsState: Record<
    SiteSearchListKey,
    SearchListExecutionState<
      SiteSearchResultItem,
      SiteSearchFilterName,
      SiteSearchSortValue,
      number | null
    >
  >
): SiteSearchSortValue | null => {
  for (const listKey of siteSearchListKeys) {
    return listsState[listKey].sort;
  }

  return null;
};

const buildOverviewUrlState = (
  definitions: SiteSearchLists,
  sharedSort: SiteSearchSortValue | null
): Record<SiteSearchListKey, SearchListUrlState<SiteSearchSortValue>> =>
  siteSearchListKeys.reduce<Record<SiteSearchListKey, SearchListUrlState<SiteSearchSortValue>>>(
    (state, listKey) => {
      state[listKey] = {
        take: definitions[listKey].pageSize,
        sort: sharedSort,
      };
      return state;
    },
    {} as Record<SiteSearchListKey, SearchListUrlState<SiteSearchSortValue>>
  );

const sanitizeActiveTab = (value: string | null): SiteSearchListKey | null => {
  if (!value) {
    return null;
  }

  return siteSearchListKeys.includes(value as SiteSearchListKey)
    ? (value as SiteSearchListKey)
    : null;
};

const createListDefinition = (
  listKey: SiteSearchListKey,
  pageSize: number,
  site: string
): SiteSearchListDefinition => ({
  pageSize,
  sortOptions: siteSearchSortOptions,
  fetcher: async ({ cursor, filters, query, signal, sort, take }) => {
    const params = new URLSearchParams();

    params.set('listKey', listKey);
    params.set('take', String(take));
    params.set('site', site);

    if (query) {
      params.set('query', query);
    }

    if (sort) {
      params.set('sort', sort);
    }

    if (cursor !== undefined && cursor !== null) {
      params.set('cursor', String(cursor));
    }

    Object.entries(filters).forEach(([filterName, values]) => {
      values.forEach((value) => {
        params.append(filterName, value);
      });
    });

    return fetch(`/api/site-search/search?${params.toString()}`, { signal }).then((response) => {
      if (!response.ok) {
        throw new Error('Unable to load site search results.');
      }

      return response.json();
    });
  },
});

const createDetailExecutionState = (definitions: SiteSearchLists) =>
  siteSearchListKeys.reduce<
    Record<
      SiteSearchListKey,
      SearchListExecutionState<
        SiteSearchResultItem,
        SiteSearchFilterName,
        SiteSearchSortValue,
        number | null
      >
    >
  >(
    (state, listKey) => {
      state[listKey] = createSearchListExecutionState(definitions[listKey], {
        services: {},
        offices: {},
      });
      return state;
    },
    {} as SiteSearchDetailStateResult['listsState']
  );

const toResultState = (
  state: SearchListExecutionState<
    SiteSearchResultItem,
    SiteSearchFilterName,
    SiteSearchSortValue,
    number | null
  >,
  loadMore: () => void
): MultiListResultState<SiteSearchResultItem, SiteSearchSortValue> => ({
  items: state.items,
  loading: state.loading,
  hasLoaded: state.hasLoaded,
  error: state.error,
  hasMore: state.hasMore,
  totalCount: state.totalCount,
  take: state.take,
  loadMore,
  sort: state.sort,
  sortOptions: siteSearchSortOptions,
});

const useSiteSearchDetailState = ({
  criteria,
  activeTab,
  lists,
}: {
  criteria: SiteSearchUrlState['criteria'];
  activeTab: SiteSearchListKey | null;
  lists: SiteSearchLists;
}): SiteSearchDetailStateResult => {
  const definitionsRef = useRef(lists);
  const criteriaRef = useRef(criteria);
  const activeTabRef = useRef(activeTab);
  const [listsState, setListsState] = useState(() => createDetailExecutionState(lists));
  const listsStateRef = useRef(listsState);
  const requestIdRef = useRef<Record<SiteSearchListKey, number>>({
    people: 0,
    services: 0,
    newsinsights: 0,
    resources: 0,
  });
  const abortControllerRef = useRef<Record<SiteSearchListKey, AbortController | undefined>>({
    people: undefined,
    services: undefined,
    newsinsights: undefined,
    resources: undefined,
  });

  definitionsRef.current = lists;
  criteriaRef.current = criteria;
  activeTabRef.current = activeTab;
  listsStateRef.current = listsState;

  const getNextRequestId = (listKey: SiteSearchListKey) => {
    const nextRequestId = requestIdRef.current[listKey] + 1;
    requestIdRef.current[listKey] = nextRequestId;
    return nextRequestId;
  };

  const abortListRequest = (listKey: SiteSearchListKey) => {
    abortControllerRef.current[listKey]?.abort();
  };

  const runReload = useCallback(
    (
      listKey: SiteSearchListKey,
      nextState: SearchListExecutionState<
        SiteSearchResultItem,
        SiteSearchFilterName,
        SiteSearchSortValue,
        number | null
      >,
      nextCriteria = criteriaRef.current
    ) => {
      const definition = definitionsRef.current[listKey];
      const requestId = getNextRequestId(listKey);

      abortListRequest(listKey);

      const controller = new AbortController();
      abortControllerRef.current[listKey] = controller;

      setListsState((currentState) => ({
        ...currentState,
        [listKey]: startSearchListReload(nextState),
      }));

      definition
        .fetcher({
          listKey,
          query: nextCriteria.query,
          filters: nextCriteria.filters,
          sort: nextState.sort,
          take: nextState.take,
          signal: controller.signal,
        })
        .then((result) => {
          if (controller.signal.aborted || requestIdRef.current[listKey] !== requestId) {
            return;
          }

          setListsState((currentState) => ({
            ...currentState,
            [listKey]: resolveSearchListReloadSuccess(currentState[listKey], definition, result),
          }));
        })
        .catch((error) => {
          if (controller.signal.aborted || requestIdRef.current[listKey] !== requestId) {
            return;
          }

          setListsState((currentState) => ({
            ...currentState,
            [listKey]: resolveSearchListReloadError(currentState[listKey], error),
          }));
        });
    },
    []
  );

  const applyUrlState = useCallback(
    (
      nextCriteria: SiteSearchUrlState['criteria'],
      nextActiveTab: SiteSearchListKey | null,
      sharedSort: SiteSearchSortValue | null,
      activeDetailTake: number | null
    ) => {
      const nextState = createDetailExecutionState(definitionsRef.current);

      siteSearchListKeys.forEach((listKey) => {
        nextState[listKey] = {
          ...nextState[listKey],
          take:
            listKey === nextActiveTab && activeDetailTake !== null
              ? activeDetailTake
              : definitionsRef.current[listKey].pageSize,
          sort: sharedSort,
        };
      });

      setListsState(nextState);

      if (nextActiveTab) {
        runReload(nextActiveTab, nextState[nextActiveTab], nextCriteria);
      }
    },
    [runReload]
  );

  const ensureTabLoaded = useCallback(
    (nextCriteria: SiteSearchUrlState['criteria'], nextActiveTab: SiteSearchListKey | null) => {
      if (!nextActiveTab) {
        return;
      }

      const currentState = listsStateRef.current[nextActiveTab];

      if (currentState.loading) {
        return;
      }

      if (currentState.hasLoaded && currentState.error === undefined) {
        return;
      }

      runReload(nextActiveTab, currentState, nextCriteria);
    },
    [runReload]
  );

  const reloadAfterCriteriaChange = useCallback(
    (nextCriteria: SiteSearchUrlState['criteria']) => {
      const nextState = siteSearchListKeys.reduce<SiteSearchDetailStateResult['listsState']>(
        (state, listKey) => {
          state[listKey] = resetSearchListExecutionForCriteriaChange(
            listsStateRef.current[listKey],
            definitionsRef.current[listKey]
          );
          return state;
        },
        {} as SiteSearchDetailStateResult['listsState']
      );

      setListsState(nextState);

      if (activeTabRef.current) {
        runReload(activeTabRef.current, nextState[activeTabRef.current], nextCriteria);
      }
    },
    [runReload]
  );

  const resetAllAndReload = useCallback(
    (nextCriteria: SiteSearchUrlState['criteria']) => {
      const nextState = createDetailExecutionState(definitionsRef.current);

      setListsState(nextState);

      if (activeTabRef.current) {
        runReload(activeTabRef.current, nextState[activeTabRef.current], nextCriteria);
      }
    },
    [runReload]
  );

  const loadMore = useCallback((listKey: SiteSearchListKey) => {
    const currentState = listsStateRef.current[listKey];

    if (!currentState.hasMore || currentState.loading || currentState.nextCursor === undefined) {
      return;
    }

    const definition = definitionsRef.current[listKey];
    const requestId = getNextRequestId(listKey);
    const nextState = startSearchListLoadMore(currentState, definition);
    const cursor = resolveSearchListLoadMoreCursor(currentState, definition);

    abortListRequest(listKey);

    const controller = new AbortController();
    abortControllerRef.current[listKey] = controller;

    setListsState((currentStateMap) => ({
      ...currentStateMap,
      [listKey]: nextState,
    }));

    definition
      .fetcher({
        listKey,
        query: criteriaRef.current.query,
        filters: criteriaRef.current.filters,
        sort: currentState.sort,
        take: definition.pageSize,
        cursor,
        signal: controller.signal,
      })
      .then((result) => {
        if (controller.signal.aborted || requestIdRef.current[listKey] !== requestId) {
          return;
        }

        setListsState((currentStateMap) => ({
          ...currentStateMap,
          [listKey]: resolveSearchListLoadMoreSuccess(currentStateMap[listKey], definition, result),
        }));
      })
      .catch((error) => {
        if (controller.signal.aborted || requestIdRef.current[listKey] !== requestId) {
          return;
        }

        setListsState((currentStateMap) => ({
          ...currentStateMap,
          [listKey]: resolveSearchListLoadMoreError(currentStateMap[listKey], definition, error),
        }));
      });
  }, []);

  const setSharedSort = useCallback(
    (nextSort: SiteSearchSortValue | null) => {
      const nextState = siteSearchListKeys.reduce<SiteSearchDetailStateResult['listsState']>(
        (state, listKey) => {
          state[listKey] = resetSearchListExecutionForSortChange(
            listsStateRef.current[listKey],
            definitionsRef.current[listKey],
            listKey === 'people' ? null : nextSort
          );
          return state;
        },
        {} as SiteSearchDetailStateResult['listsState']
      );

      setListsState(nextState);

      if (activeTabRef.current) {
        runReload(activeTabRef.current, nextState[activeTabRef.current]);
      }
    },
    [runReload]
  );

  useEffect(() => {
    return () => {
      Object.values(abortControllerRef.current).forEach((controller) => controller?.abort());
    };
  }, []);

  return {
    listsState,
    applyUrlState,
    ensureTabLoaded,
    reloadAfterCriteriaChange,
    resetAllAndReload,
    loadMore,
    setSharedSort,
  };
};

export const useSiteSearch = ({
  previewResultsPerPage,
  detailResultsPerPage,
  filters,
}: UseSiteSearchConfig) => {
  const { site, isEastAsiaSite } = useSite();
  const filterDefinitions = useMemo(() => toFilterDefinitions(filters), [filters]);
  const [viewState, setViewState] = useState<SiteSearchViewState>({
    activeTab: null,
  });
  const criteriaState = useSearchCriteria({
    filters: filterDefinitions,
  });
  const criteriaRef = useRef(criteriaState.appliedCriteria);
  const currentUrlStateRef = useRef<SiteSearchUrlState | null>(null);
  const applyUrlStateRef = useRef<(urlState: SiteSearchUrlState) => void>(() => {});

  const overviewLists = useMemo<SiteSearchLists>(
    () =>
      siteSearchListKeys.reduce<SiteSearchLists>((definitions, listKey) => {
        definitions[listKey] = createListDefinition(listKey, previewResultsPerPage[listKey], site);
        return definitions;
      }, {} as SiteSearchLists),
    [previewResultsPerPage, site]
  );

  const detailLists = useMemo<SiteSearchLists>(
    () =>
      siteSearchListKeys.reduce<SiteSearchLists>((definitions, listKey) => {
        definitions[listKey] = createListDefinition(listKey, detailResultsPerPage[listKey], site);
        return definitions;
      }, {} as SiteSearchLists),
    [detailResultsPerPage, site]
  );

  const overviewExecution = useMultiSearchListState({
    enabled: false,
    criteria: criteriaState.appliedCriteria,
    lists: overviewLists,
    listKeys: [...siteSearchListKeys],
  });
  const detailExecution = useSiteSearchDetailState({
    criteria: criteriaState.appliedCriteria,
    activeTab: viewState.activeTab,
    lists: detailLists,
  });

  criteriaRef.current = criteriaState.appliedCriteria;

  const readUrlStateFromAsPath = useCallback(
    (asPath: string): SiteSearchUrlState => {
      const criteria = readSearchCriteriaUrlStateFromAsPath({
        asPath,
        filterDefinitions,
        filterNames: criteriaState.filterNames,
      });
      const params = new URL(asPath, 'https://example.com').searchParams;
      const activeTab = sanitizeActiveTab(params.get(siteSearchTabParamName));
      const activeDetailState = activeTab
        ? readSearchListUrlStateFromAsPath({
            asPath,
            pageSize: detailLists[activeTab].pageSize,
            ...resolveSearchListParamNames({
              listKey: activeTab,
              namespaced: true,
            }),
            sortOptions: detailLists[activeTab].sortOptions,
            defaultSort: detailLists[activeTab].defaultSort,
          })
        : null;

      return {
        criteria,
        activeTab,
        sharedSort: activeDetailState?.sort ?? null,
        activeDetailTake: activeTab
          ? (activeDetailState?.take ?? detailLists[activeTab].pageSize)
          : null,
      };
    },
    [criteriaState.filterNames, detailLists, filterDefinitions]
  );

  const serializeUrlState = useCallback(
    (urlState: SiteSearchUrlState) =>
      JSON.stringify({
        criteria: JSON.parse(serializeCriteria(urlState.criteria, criteriaState.filterNames)),
        activeTab: urlState.activeTab,
        sharedSort: urlState.sharedSort,
        activeDetailTake: urlState.activeDetailTake,
        detail: urlState.activeTab
          ? JSON.parse(
              serializeListUrlState({
                take: urlState.activeDetailTake ?? detailLists[urlState.activeTab].pageSize,
                sort: urlState.sharedSort,
              })
            )
          : null,
      }),
    [criteriaState.filterNames, detailLists]
  );

  const buildSearchParams = useCallback(
    (urlState: SiteSearchUrlState) => {
      const params = new URLSearchParams();

      appendSearchCriteriaParams({
        params,
        urlState: urlState.criteria,
        filterDefinitions,
        filterNames: criteriaState.filterNames,
      });

      if (urlState.activeTab) {
        params.set(siteSearchTabParamName, urlState.activeTab);

        const activeTab = urlState.activeTab;
        const { takeParamName, sortParamName } = resolveSearchListParamNames({
          listKey: activeTab,
          namespaced: true,
        });

        appendSearchListParams({
          params,
          urlState: {
            take: urlState.activeDetailTake ?? detailLists[activeTab].pageSize,
            sort: urlState.sharedSort,
          },
          pageSize: detailLists[activeTab].pageSize,
          takeParamName,
          sortParamName,
          defaultSort: detailLists[activeTab].defaultSort,
        });
      }

      return params;
    },
    [criteriaState.filterNames, detailLists, filterDefinitions]
  );

  const { hasHydratedFromUrl, setNavigationMode } = useSearchPageUrlSync({
    readUrlStateFromAsPath,
    applyUrlState: (urlState) => applyUrlStateRef.current(urlState),
    getCurrentUrlState: () => currentUrlStateRef.current ?? readUrlStateFromAsPath(''),
    serializeUrlState,
    buildSearchParams,
  });

  // Single owner of the accordion-visibility predicate: consumed both as the facets-fetch gate below and by
  // `CoreSiteSearch.tsx` for rendering — never maintain two copies of this condition. The
  // gating matches the render surface: accordions never render on eastasia, and on main they
  // render only with a non-empty applied query (a filters-only deep link must not pay the
  // facet fan-out for UI that isn't shown).
  const canShowFilterAccordions = !isEastAsiaSite && Boolean(criteriaState.query);

  const facetsFetcher = useMemo<SearchFacetsFetcher<SiteSearchFilterName>>(
    () =>
      async (criteria, { signal }) => {
        const params = new URLSearchParams();

        params.set('listKey', 'all');
        params.set('facetsOnly', 'true');
        params.set('site', site);

        if (criteria.query) {
          params.set('query', criteria.query);
        }

        Object.entries<string[]>(criteria.filters).forEach(([filterName, values]) => {
          values.forEach((value) => {
            params.append(filterName, value);
          });
        });

        const response = await fetch(`/api/site-search/search?${params.toString()}`, { signal });

        if (!response.ok) {
          throw new Error('Unable to load site search facets.');
        }

        return response.json();
      },
    [site]
  );

  // Facets are mode-independent (union across lists, by design): keyed off the applied
  // criteria only, so overview/detail/tab transitions never refetch or reset them. Site
  // deliberately ships no build-time seed.
  const facetsState = useSearchFacetsState<SiteSearchFilterName>({
    enabled: hasHydratedFromUrl && canShowFilterAccordions,
    serializedCriteria: serializeCriteria(criteriaState.appliedCriteria, criteriaState.filterNames),
    criteria: criteriaState.appliedCriteria,
    fetcher: facetsFetcher,
    initialFacetState: undefined,
  });

  applyUrlStateRef.current = (urlState) => {
    criteriaState.applyUrlState(urlState.criteria);
    setViewState({
      activeTab: urlState.activeTab,
    });
    overviewExecution.applyUrlState(
      urlState.criteria,
      buildOverviewUrlState(overviewLists, urlState.sharedSort)
    );
    detailExecution.applyUrlState(
      urlState.criteria,
      urlState.activeTab,
      urlState.sharedSort,
      urlState.activeDetailTake
    );
  };

  const criteriaController = useSearchPageCriteriaController({
    criteriaState,
    filterDefinitions,
    getCurrentCriteriaState: () => ({
      queryDraft: criteriaState.queryDraft,
      query: criteriaState.query,
      selectedFilters: criteriaState.selectedFilters,
    }),
    setNavigationMode,
    onCriteriaChange: (nextCriteria) => {
      overviewExecution.reloadAfterCriteriaChange(nextCriteria);
      detailExecution.reloadAfterCriteriaChange(nextCriteria);
    },
    onClearAll: (nextCriteria) => {
      overviewExecution.resetAllAndReload(nextCriteria);
      detailExecution.resetAllAndReload(nextCriteria);
    },
  });

  const normalizedSharedSort =
    viewState.activeTab !== null
      ? detailExecution.listsState[viewState.activeTab].sort
      : resolveSharedSortFromOverviewState(overviewExecution.listsState);

  const setSort = useCallback(
    (nextSort: SiteSearchSortValue) => {
      const normalizedSort =
        nextSort === getSharedDisplayedSort(criteriaState.query, null) ? null : nextSort;

      setNavigationMode('replace');
      detailExecution.setSharedSort(normalizedSort);
    },
    [criteriaState.query, detailExecution, overviewExecution, setNavigationMode]
  );

  const openDetailTab = useCallback(
    (listKey: SiteSearchListKey) => {
      setNavigationMode('push');
      setViewState({
        activeTab: listKey,
      });
    },
    [setNavigationMode]
  );

  const showOverview = useCallback(() => {
    setNavigationMode('push');
    setViewState({
      activeTab: null,
    });
  }, [setNavigationMode]);

  const submitQuery = (nextQuery = criteriaState.queryDraft) => {
    const nextCriteria = getAppliedCriteria(
      submitSearchCriteriaQuery(
        {
          queryDraft: criteriaState.queryDraft,
          query: criteriaState.query,
          selectedFilters: criteriaState.selectedFilters,
        },
        criteriaState.filterNames,
        nextQuery
      )
    );

    setNavigationMode('push');
    criteriaState.submitQuery(nextQuery);
    setViewState({
      activeTab: null,
    });
    overviewExecution.reloadAfterCriteriaChange(nextCriteria);
    detailExecution.reloadAfterCriteriaChange(nextCriteria);
  };

  useEffect(() => {
    if (!hasHydratedFromUrl) {
      return;
    }

    detailExecution.ensureTabLoaded(criteriaRef.current, viewState.activeTab);
  }, [detailExecution.ensureTabLoaded, hasHydratedFromUrl, viewState.activeTab]);

  currentUrlStateRef.current = {
    criteria: criteriaRef.current,
    activeTab: viewState.activeTab,
    sharedSort: normalizedSharedSort,
    activeDetailTake:
      viewState.activeTab !== null ? detailExecution.listsState[viewState.activeTab].take : null,
  };

  const overviewResults = useMemo(
    () =>
      siteSearchListKeys.reduce<
        Record<SiteSearchListKey, MultiListResultState<SiteSearchResultItem, SiteSearchSortValue>>
      >(
        (result, listKey) => {
          result[listKey] = toResultState(overviewExecution.listsState[listKey], () =>
            openDetailTab(listKey)
          );
          return result;
        },
        {} as Record<
          SiteSearchListKey,
          MultiListResultState<SiteSearchResultItem, SiteSearchSortValue>
        >
      ),
    [openDetailTab, overviewExecution.listsState]
  );

  const activeDetailList = viewState.activeTab
    ? toResultState(detailExecution.listsState[viewState.activeTab], () => {
        setNavigationMode('replace');
        detailExecution.loadMore(viewState.activeTab as SiteSearchListKey);
      })
    : null;

  return {
    query: criteriaState.query,
    queryDraft: criteriaState.queryDraft,
    setQueryDraft: criteriaState.setQueryDraft,
    submitQuery,
    clearQuery: criteriaController.clearQuery,
    filters: criteriaController.filters,
    facetState: facetsState.facetState,
    isFetching: facetsState.isFetching,
    canShowFilterAccordions,
    activeFilters: criteriaController.activeFilters,
    hasActiveFilters: criteriaController.hasActiveFilters,
    clearFilter: criteriaController.clearFilter,
    clearAllFilters: criteriaController.clearAllFilters,
    clearAll: criteriaController.clearAll,
    mode: viewState.activeTab ? 'detail' : 'overview',
    activeTab: viewState.activeTab,
    overviewLists: overviewResults,
    activeDetailList,
    openDetailTab,
    showOverview,
    setSort,
    sort: getSharedDisplayedSort(
      criteriaState.query,
      normalizedSharedSort as SiteSearchSortValue | null
    ),
  };
};
